import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  ReactNode,
} from "react";
import {
  getAllExperiences,
  Experience,
  createExperience,
  updateExperience,
  deleteExperience,
  moveExperienceUp,
  moveExperienceDown,
} from "../firebase/experienceService";
import { seedExperiences } from "../firebase/seedExperiences";
import { Timestamp } from "firebase/firestore";
import { withFirebaseRetry } from "../utils/retryUtils";
import { globalRateLimiter } from "../utils/rateLimiter";

interface ExperienceContextType {
  experiences: Experience[];
  loading: boolean;
  error: Error | null;
  refreshExperiences: () => Promise<void>;
  addExperience: (
    experience: Omit<Experience, "id" | "createdAt" | "updatedAt">
  ) => Promise<string>;
  editExperience: (
    id: string,
    experience: Partial<Omit<Experience, "id" | "createdAt" | "updatedAt">>
  ) => Promise<void>;
  removeExperience: (id: string) => Promise<void>;
  moveUp: (id: string) => Promise<void>;
  moveDown: (id: string) => Promise<void>;
}

const ExperienceContext = createContext<ExperienceContextType | undefined>(
  undefined
);

export const ExperienceProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [experiences, setExperiences] = useState<Experience[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const [lastFetchTime, setLastFetchTime] = useState<number>(0);

  // Cache duration in milliseconds (5 minutes)
  const CACHE_DURATION = 5 * 60 * 1000;

  const fetchExperiences = async (forceRefresh: boolean = false) => {
    // Check if we have cached data and it's still fresh
    const now = Date.now();
    const isCacheValid =
      experiences.length > 0 && now - lastFetchTime < CACHE_DURATION;

    if (!forceRefresh && isCacheValid) {
      console.log("Using cached experience data");
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Use retry mechanism for fetching experiences
      const experiencesData = await withFirebaseRetry(async () => {
        // Attempt to seed the collection if it's empty
        await seedExperiences();
        return await getAllExperiences();
      });

      setExperiences(experiencesData);
      setLastFetchTime(now);
    } catch (err) {
      setError(
        err instanceof Error
          ? err
          : new Error("Failed to fetch experiences data")
      );
      console.error("Error fetching experiences:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchExperiences();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const refreshExperiences = async () => {
    await fetchExperiences(true); // Force refresh
  };

  const addExperience = async (
    experience: Omit<Experience, "id" | "createdAt" | "updatedAt">
  ): Promise<string> => {
    // Rate limiting for experience operations
    if (!globalRateLimiter.isAllowed("add-experience")) {
      throw new Error(
        "Too many requests. Please wait a moment before trying again."
      );
    }

    const id = await createExperience(experience);
    // Instead of refreshing all experiences, add the new experience to the state
    const newExperience = {
      ...experience,
      id,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    };
    setExperiences((prevExperiences) => [...prevExperiences, newExperience]);
    return id;
  };

  const editExperience = async (
    id: string,
    experience: Partial<Omit<Experience, "id" | "createdAt" | "updatedAt">>
  ): Promise<void> => {
    // Rate limiting for experience updates
    if (!globalRateLimiter.isAllowed(`edit-experience-${id}`)) {
      throw new Error(
        "Too many update requests. Please wait a moment before trying again."
      );
    }

    await updateExperience(id, experience);
    // Instead of refreshing all experiences, update the specific experience in state
    setExperiences((prevExperiences) =>
      prevExperiences.map((e) =>
        e.id === id
          ? ({ ...e, ...experience, updatedAt: Timestamp.now() } as Experience)
          : e
      )
    );
  };

  const removeExperience = async (id: string): Promise<void> => {
    await deleteExperience(id);
    // Instead of refreshing all experiences, remove the specific experience from state
    setExperiences((prevExperiences) =>
      prevExperiences.filter((e) => e.id !== id)
    );
  };

  const moveUp = async (id: string): Promise<void> => {
    await moveExperienceUp(id);
    // For move operations, we need to refresh to get proper ordering
    // But we could optimize this further by handling the reordering in state
    await refreshExperiences();
  };

  const moveDown = async (id: string): Promise<void> => {
    await moveExperienceDown(id);
    // For move operations, we need to refresh to get proper ordering
    // But we could optimize this further by handling the reordering in state
    await refreshExperiences();
  };

  return (
    <ExperienceContext.Provider
      value={{
        experiences,
        loading,
        error,
        refreshExperiences,
        addExperience,
        editExperience,
        removeExperience,
        moveUp,
        moveDown,
      }}
    >
      {children}
    </ExperienceContext.Provider>
  );
};

export const useExperiences = (): ExperienceContextType => {
  const context = useContext(ExperienceContext);
  if (context === undefined) {
    throw new Error("useExperiences must be used within an ExperienceProvider");
  }
  return context;
};
